/**
 * Subclass of IntAnimation with step-wise interpolation between keyframes.
 *
 * Each keyframe has a fraction [0-1] and an integer value. Between keyframes,
 * the value steps from the first keyframe's value to the second keyframe's value
 * based on the stepThreshold. If the fraction within the segment is less than
 * the threshold, the first value is used; otherwise, the second value is used.
 *
 * TypeScript port of C++ IntStepAnimation from
 * tabanim/public/animation/IntStepAnimation.h
 */

import { IntAnimation, IntRef } from './IntAnimation';
import { KeyframeFractionUtils } from './KeyframeFractionUtils';

/**
 * Represents a single integer keyframe with fraction and value.
 */
export interface IntKeyframe {
  fraction: number;
  value: number;
}

/**
 * Default step threshold. The animation steps from the first value to the second
 * when the segment fraction exceeds this threshold.
 */
const DEFAULT_STEP_THRESHOLD = 0.0001; // Approximates C++ COMPARISON_EPSILON * 2

export class IntStepAnimation extends IntAnimation {
  private stepThreshold: number = DEFAULT_STEP_THRESHOLD;
  private keyframes: IntKeyframe[] = [];

  /**
   * Create an IntStepAnimation with the given output value reference.
   *
   * @param outValue Reference object that will be updated with the animated value
   */
  constructor(outValue: IntRef) {
    super(outValue);
  }

  /**
   * Convenience constructor for simple ramp from startValue to endValue.
   *
   * @param outValue Reference object that will be updated with the animated value
   * @param startValue Value at fraction 0.0
   * @param endValue Value at fraction 1.0
   */
  public static createRamp(outValue: IntRef, startValue: number, endValue: number): IntStepAnimation {
    const animation = new IntStepAnimation(outValue);
    animation.addKeyframe(0.0, startValue);
    animation.addKeyframe(1.0, endValue);
    return animation;
  }

  /**
   * Get the step threshold.
   * When the fraction within a keyframe segment exceeds this threshold,
   * the animation steps from the first value to the second value.
   */
  public getStepThreshold(): number {
    return this.stepThreshold;
  }

  public setStepThreshold(threshold: number): void {
    this.stepThreshold = threshold;
  }

  /**
   * Add a keyframe value at the specified fraction.
   * Replaces any keyframe that already exists at that fraction.
   * Clamps the incoming fraction to [0, 1].
   * Ensures keyframes remain sorted by fraction (monotonically increasing).
   */
  public addKeyframe(fraction: number, value: number): void {
    fraction = Math.max(0, Math.min(1, fraction));
    value = Math.round(value);

    let inserted = false;
    for (let i = 0; i < this.keyframes.length; i++) {
      if (this.keyframes[i].fraction === fraction) {
        this.keyframes[i].value = value;
        inserted = true;
        break;
      } else if (fraction < this.keyframes[i].fraction) {
        this.keyframes.splice(i, 0, { fraction, value });
        inserted = true;
        break;
      }
    }

    if (!inserted) {
      this.keyframes.push({ fraction, value });
    }
  }

  public removeKeyframe(fraction: number): void {
    const index = this.keyframes.findIndex((kf) => kf.fraction === fraction);
    if (index !== -1) {
      this.keyframes.splice(index, 1);
    }
  }

  public clearKeyframes(): void {
    this.keyframes = [];
  }

  public getKeyframes(): IntKeyframe[] {
    return this.keyframes.map((kf) => ({ ...kf }));
  }

  public update(): void {
    const outValue = this.getValue();
    if (!outValue) {
      return;
    }

    if (this.keyframes.length === 0) {
      return;
    }

    const fraction = this.getFraction();
    let kfIndex = KeyframeFractionUtils.getFractionArrayIndexBeforeOrAt(this.keyframes, fraction);

    if (kfIndex < 0) {
      kfIndex = 0;
    }

    if (kfIndex >= this.keyframes.length - 1) {
      this.setValueFromInt(this.keyframes[kfIndex].value);
    } else {
      const segmentFraction = KeyframeFractionUtils.mapFractionToInterval(this.keyframes, fraction, kfIndex, kfIndex + 1);

      if (segmentFraction < this.stepThreshold) {
        this.setValueFromInt(this.keyframes[kfIndex].value);
      } else {
        this.setValueFromInt(this.keyframes[kfIndex + 1].value);
      }
    }
  }
}
